import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import SocialLinks from "./SocialLinks";
import LanguageSwitcher from "./LanguageSwitcher";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const navigateToPage = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <footer className="bg-brand-black text-white pt-16 pb-8">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div>
            <Logo withText={false} size="lg" />
            <h3 className="text-xl font-bold mt-4 mb-4 bg-gradient-to-r from-brand-yellow to-brand-yellow/70 bg-clip-text text-transparent">
              Prebitit
            </h3>
            <p className="text-gray-300 mb-6">{t("common.footerTitle")}</p>
            <SocialLinks variant="footer" iconSize={24} />
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4">{t("common.services")}</h3>
            <ul className="gap-y-3">
              <li>
                <button
                  onClick={() => navigateToPage("/services/websites")}
                  className={`${
                    isActive("/services/websites")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("services.websites")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/services/apps")}
                  className={`${
                    isActive("/services/apps")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("services.apps")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/services/ai")}
                  className={`${
                    isActive("/services/ai")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("services.ai")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/services/cameras")}
                  className={`${
                    isActive("/services/cameras")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("services.cameras")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/services/pc")}
                  className={`${
                    isActive("/services/pc")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("services.pc")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/services/courses")}
                  className={`${
                    isActive("/services/courses")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("services.courses")}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4">{t("footer.company")}</h3>
            <ul className="gap-y-3">
              <li>
                <button
                  onClick={() => navigateToPage("/about")}
                  className={`${
                    isActive("/about")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("footer.about")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/portfolio")}
                  className={`${
                    isActive("/portfolio")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("common.portfolio")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/contact")}
                  className={`${
                    isActive("/contact")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("common.contact")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/careers")}
                  className={`${
                    isActive("/careers")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("footer.careers")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/privacy")}
                  className={`${
                    isActive("/privacy")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("footer.privacy")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage("/terms")}
                  className={`${
                    isActive("/terms")
                      ? "text-primary"
                      : "text-gray-300 hover:text-white"
                  } transition-colors`}
                >
                  {t("footer.terms")}
                </button>
              </li>
            </ul>
          </div>

          <div id="contact">
            <h3 className="text-lg font-bold mb-4">{t("footer.contactUs")}</h3>
            <ul className="gap-y-3">

              <li className="flex items-center gap-2 text-gray-300">
                <FaWhatsapp size={16} />
                <a
                  href="https://wa.me/905076421214"
                  className="text-white hover:text-brand-yellow transition-colors"
                  dir="ltr"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  +90 507 642 12 14
                </a>
              </li>
              <li className="flex items-center gap-2 text-gray-300">
                <Phone size={16} />
                <a
                  href="tel:+905076421214"
                  className="text-white hover:text-brand-yellow transition-colors"
                  dir="ltr"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  +90 507 642 12 14
                </a>
              </li>

              <li className="flex items-start gap-2 text-gray-300">
                <MapPin size={16} className="mt-1 flex-shrink-0" />
                <a
                  href="https://maps.google.com/?q=Istanbul,Turkey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-brand-yellow transition-colors"
                >
                  {t("footer.location")}
                </a>
              </li>
              <li className="flex items-center gap-2 text-gray-300">
                <Mail size={16} />
                <a
                  href="mailto:info@prebitit.com"
                  className="text-white hover:text-brand-yellow transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  info@prebitit.com
                </a>
              </li>
              <li className="mt-4 pt-4 border-t border-gray-800 flex items-center justify-between">
                <a
                  href="https://www.instagram.com/prebitit/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${
                    isActive("/")
                      ? "text-primary"
                      : "text-brand-yellow hover:text-brand-yellow/80"
                  } transition-colors`}
                >
                  <span className="font-medium">{t("common.instagram")}</span>
                </a>
                <LanguageSwitcher variant="minimal" />
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <p>
              © {currentYear} Prebitit. {t("footer.allRightsReserved")}
            </p>
            <span className="text-gray-400">•</span>
            <p>v2.3.1</p>
            <span className="text-gray-400">•</span>
            <button
              onClick={() => navigateToPage("/admin/login")}
              className="text-gray-400 hover:text-white transition-colors text-xs"
            >
              Admin
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
